import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, openAuthorizedFile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, FileText, Download, Ban, Info, ExternalLink, Link2, FileType, FileCode2,
} from "lucide-react";
import { money, SUPPLIER_DOC_TYPES, SUPPLIER_DOC_STATUSES, STATUS_COLORS } from "@/lib/constants";


function InfoRow({ label, value, mono = false, testId }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`col-span-2 text-sm ${mono ? "mono" : ""}`} data-testid={testId}>{value || "—"}</div>
    </div>
  );
}

export default function DocumentoProveedorDetalle() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [saving, setSaving] = useState(false);

  const canCancel = hasPermission("supplier_documents:cancel");

  async function fetchDoc() {
    setLoading(true);
    try {
      const { data } = await api.get(`/supplier-documents/${docId}`);
      setDoc(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchDoc(); /* eslint-disable-next-line */ }, [docId]);

  async function submitCancel() {
    if (!cancelReason.trim()) { toast.error("Motivo obligatorio"); return; }
    setSaving(true);
    try {
      await api.post(`/supplier-documents/${docId}/cancel`, { reason: cancelReason.trim() });
      toast.success("Documento cancelado");
      setCancelOpen(false);
      setCancelReason("");
      fetchDoc();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-64" />
    </div>
  );
  if (!doc) return null;

  const typeLabel = SUPPLIER_DOC_TYPES.find((t) => t.value === doc.doc_type)?.label || doc.doc_type;
  const statusMeta = SUPPLIER_DOC_STATUSES.find((s) => s.value === doc.status) || { tone: "slate" };
  const isNC = doc.doc_type === "nota_credito";
  const pdfHref = doc.pdf_url ? `/supplier-documents/${doc.id}/file/pdf` : null;
  const xmlHref = doc.xml_url ? `/supplier-documents/${doc.id}/file/xml` : null;
  const openFile = (path, filename) => openAuthorizedFile(path, filename).catch((e) => toast.error(formatApiError(e)));

  return (
    <div className="space-y-6" data-testid="page-supplier-doc-detail">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/documentos-proveedores")} data-testid="btn-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Documentos
        </Button>
      </div>

      <PageHeader
        title={
          <div className="flex items-center gap-3 flex-wrap">
            <FileText className="h-5 w-5" />
            <span>{typeLabel}</span>
            <Badge className={STATUS_COLORS[statusMeta.tone]} data-testid="sd-status-badge">
              {SUPPLIER_DOC_STATUSES.find((s) => s.value === doc.status)?.label || doc.status}
            </Badge>
            {doc.external_folio && (
              <span className="mono text-sm text-muted-foreground">
                {[doc.external_serie, doc.external_folio].filter(Boolean).join("-")}
              </span>
            )}
          </div>
        }
        description={doc.supplier_name}
        actions={
          canCancel && doc.status === "activo" ? (
            <Button variant="outline" onClick={() => setCancelOpen(true)} data-testid="btn-cancel-doc">
              <Ban className="h-4 w-4 mr-2" /> Cancelar
            </Button>
          ) : null
        }
      />

      {doc.status === "cancelado" && (
        <Alert variant="destructive" data-testid="cancelled-alert">
          <Info className="h-4 w-4" />
          <AlertTitle>Documento cancelado</AlertTitle>
          <AlertDescription>
            {doc.cancel_reason || "—"}
            {doc.cancelled_by_name && <span className="block text-xs mt-1">Por {doc.cancelled_by_name} · {doc.cancelled_at?.slice(0, 10)}</span>}
          </AlertDescription>
        </Alert>
      )}

      {isNC && (
        <Alert data-testid="nc-info-alert">
          <Info className="h-4 w-4" />
          <AlertTitle>Nota de crédito — evidencia documental</AlertTitle>
          <AlertDescription>
            Este documento se guarda solo como <strong>evidencia administrativa</strong>. El monto acreditado es
            informativo y <strong>no modifica automáticamente</strong> cuentas por pagar ni genera saldos a favor.
            Aplica el crédito con tu proveedor por fuera del sistema.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 space-y-1 col-span-1 md:col-span-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Referencias externas</div>
          <InfoRow label="Serie" value={doc.external_serie} mono testId="ref-serie" />
          <InfoRow label="Folio" value={doc.external_folio} mono testId="ref-folio" />
          <InfoRow label="UUID / Folio fiscal" value={doc.external_uuid} mono testId="ref-uuid" />
          <InfoRow label="Fecha de emisión" value={doc.issue_date} testId="ref-date" />
          <InfoRow label="Notas" value={doc.notes} testId="ref-notes" />
        </Card>

        <Card className="p-4 space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Montos</div>
          <InfoRow label="Subtotal" value={money(doc.subtotal)} mono testId="amt-subtotal" />
          <InfoRow label="IVA" value={money(doc.iva)} mono testId="amt-iva" />
          <InfoRow label="Total" value={money(doc.total)} mono testId="amt-total" />
          {isNC && (
            <InfoRow
              label="Monto acreditado"
              value={<span>{money(doc.credited_amount)} <span className="text-xs text-amber-700">(informativo)</span></span>}
              testId="amt-credited"
            />
          )}
        </Card>
      </div>

      {/* Archivos adjuntos */}
      <Card className="p-4 space-y-3" data-testid="files-card">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Archivos adjuntos</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pdfHref ? (
            <div className="border border-border rounded-lg p-3 space-y-2" data-testid="file-pdf">
              <div className="flex items-center gap-2">
                <FileType className="h-5 w-5 text-red-600" />
                <span className="text-sm font-medium">PDF</span>
                {doc.pdf_size && <span className="text-xs text-muted-foreground">· {(doc.pdf_size / 1024).toFixed(1)} KB</span>}
              </div>
              <div className="text-xs text-muted-foreground truncate">{doc.pdf_filename}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openFile(pdfHref)} data-testid="btn-open-pdf">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Abrir
                </Button>
                <Button size="sm" variant="outline" onClick={() => openFile(pdfHref, doc.pdf_filename || "documento.pdf")} data-testid="btn-download-pdf">
                  <Download className="h-3.5 w-3.5 mr-1" /> Descargar
                </Button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border rounded-lg p-3 text-sm text-muted-foreground text-center">Sin PDF</div>
          )}
          {xmlHref ? (
            <div className="border border-border rounded-lg p-3 space-y-2" data-testid="file-xml">
              <div className="flex items-center gap-2">
                <FileCode2 className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium">XML</span>
                {doc.xml_size && <span className="text-xs text-muted-foreground">· {(doc.xml_size / 1024).toFixed(1)} KB</span>}
              </div>
              <div className="text-xs text-muted-foreground truncate">{doc.xml_filename}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openFile(xmlHref, doc.xml_filename || "documento.xml")} data-testid="btn-download-xml">
                  <Download className="h-3.5 w-3.5 mr-1" /> Descargar XML
                </Button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border rounded-lg p-3 text-sm text-muted-foreground text-center">Sin XML</div>
          )}
        </div>
      </Card>

      {/* Vínculos */}
      <Card className="p-4 space-y-2" data-testid="links-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Link2 className="h-3.5 w-3.5" /> Vínculos administrativos
        </div>
        {!doc.purchase_order_id && !doc.accounts_payable_id && !doc.purchase_return_id ? (
          <div className="text-sm text-muted-foreground">Este documento no está vinculado a ninguna OC, CxP ni devolución.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {doc.purchase_order_id && (
              <div className="border border-border rounded-md p-3" data-testid="link-po">
                <div className="text-xs text-muted-foreground">Orden de compra</div>
                <button
                  onClick={() => navigate(`/compras/${doc.purchase_order_id}`)}
                  className="mono text-sm text-tenant hover:underline"
                >
                  {doc.purchase_order_folio || doc.purchase_order_id}
                </button>
              </div>
            )}
            {doc.accounts_payable_id && (
              <div className="border border-border rounded-md p-3" data-testid="link-ap">
                <div className="text-xs text-muted-foreground">Cuenta por pagar</div>
                <button
                  onClick={() => navigate(`/cuentas-por-pagar/${doc.accounts_payable_id}`)}
                  className="text-sm text-tenant hover:underline"
                >
                  Ir a CxP {doc.accounts_payable_status ? `(${doc.accounts_payable_status})` : ""}
                </button>
              </div>
            )}
            {doc.purchase_return_id && (
              <div className="border border-border rounded-md p-3" data-testid="link-pr">
                <div className="text-xs text-muted-foreground">Devolución</div>
                <button
                  onClick={() => navigate(`/devoluciones/${doc.purchase_return_id}`)}
                  className="mono text-sm text-tenant hover:underline"
                >
                  {doc.purchase_return_folio || doc.purchase_return_id}
                </button>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="text-xs text-muted-foreground">
        Subido por {doc.uploaded_by_name || "—"} · {doc.created_at?.slice(0, 16).replace("T", " ")}
      </div>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="dlg-cancel-doc">
          <DialogHeader>
            <DialogTitle>Cancelar documento</DialogTitle>
            <DialogDescription>Motivo (obligatorio). El archivo permanecerá guardado pero se marca como cancelado.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label>Motivo</Label>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={saving} data-testid="cancel-cancel">Volver</Button>
            <Button onClick={submitCancel} disabled={saving} variant="destructive" data-testid="cancel-submit">
              {saving ? "Cancelando…" : "Confirmar cancelación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
